import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../icons';
import type { MobileShellCtx } from './MobileShellCtx';
import { SearchOverlay } from './SearchOverlay';
import { AIEventSheet } from './sheets/AIEventSheet';
import { AlertEventSheet } from './sheets/AlertEventSheet';
import { AlertRuleEditSheet } from './sheets/AlertRuleEditSheet';
import { AlertRuleSheet } from './sheets/AlertRuleSheet';
import { BucketLogsSheet } from './sheets/BucketLogsSheet';
import { FilterSheet } from './sheets/FilterSheet';
import { LogDetailSheet } from './sheets/LogDetailSheet';

export function BottomSheet({
  title,
  onClose,
  children,
  action,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const [closing, setClosing] = useState(false);
  const dragRef = useRef<{ startY: number; currentY: number } | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(onClose, 200);
  }, [closing, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [close]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = { startY: e.clientY, currentY: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !sheetRef.current) return;
    dragRef.current.currentY = e.clientY;
    const dy = Math.max(0, e.clientY - dragRef.current.startY);
    sheetRef.current.style.transform = `translateY(${dy}px)`;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !sheetRef.current) return;
    const dy = dragRef.current.currentY - dragRef.current.startY;
    sheetRef.current.style.transform = '';
    dragRef.current = null;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    if (dy > 80) close();
  };

  return (
    <>
      <button type="button" className="sheet-backdrop" aria-label="Close sheet" onClick={close} />
      {/* Not a <dialog>: the sheet is drag-dismissable and animates from the bottom
          edge, which means it manages its own position rather than living in the
          top layer. role="dialog" gives assistive tech the same information without
          inheriting the element's modal behaviour. */}
      {/* biome-ignore lint/a11y/useSemanticElements: a <dialog> would take over positioning and the top layer; this sheet animates and drags on its own. */}
      <div ref={sheetRef} className={`sheet ${closing ? 'closing' : ''}`} role="dialog">
        <div
          className="sheet-grabber"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: 'none' }}
        />
        <div className="sheet-head">
          <div className="sheet-title">{title}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {action}
            <button type="button" className="sheet-close" onClick={close} aria-label="Close">
              <Icon.X size={15} />
            </button>
          </div>
        </div>
        <div className="sheet-body">{children}</div>
      </div>
    </>
  );
}

export function BottomSheetHost({ ctx }: { ctx: MobileShellCtx }) {
  if (!ctx.sheet) return null;
  const s = ctx.sheet;
  if (s.kind === 'log') return <LogDetailSheet ctx={ctx} logId={s.logId} />;
  if (s.kind === 'alertEvent') return <AlertEventSheet ctx={ctx} eventId={s.eventId} />;
  if (s.kind === 'alertRule') return <AlertRuleSheet ctx={ctx} ruleId={s.ruleId} />;
  if (s.kind === 'alertRuleEdit') return <AlertRuleEditSheet ctx={ctx} ruleId={s.ruleId} />;
  if (s.kind === 'aiEvent') return <AIEventSheet ctx={ctx} eventIdx={s.eventIdx} />;
  if (s.kind === 'filter') return <FilterSheet ctx={ctx} />;
  if (s.kind === 'bucketLogs')
    return (
      <BucketLogsSheet
        ctx={ctx}
        fromMs={s.fromMs}
        size={s.size}
        applicationId={s.applicationId}
        applicationIds={s.applicationIds}
      />
    );
  if (s.kind === 'search') return <SearchOverlay ctx={ctx} />;
  return null;
}
